import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import TenantPermissionService from "@/services/APIs/TenantPermissionService";
import { gridName } from "@/utils/constant/gridName";
import { tableName } from "@/utils/constant/tableName";
import { Checkbox, Popconfirm } from "antd";
import { Edit2, Trash } from "lucide-react";
import { useMemo, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import PermissionModal from "./PermissionModal";

/** Compact inline checkbox row — no Form.Item margins */
function PermCheck({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, lineHeight: "22px" }}>
      <Checkbox checked={checked} disabled />
      <span style={{ fontSize: 12 }}>{label}</span>
    </div>
  );
}

interface PermissionsTableProps {
  permissions: any;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  permissionsLoading: boolean;
  roleOptions: any[];
  schemaOptions: any[];
  toolBarRender: () => React.ReactNode[];
  refetchPermissions: () => void;
}

const PermissionsTable = ({
  permissions,
  page,
  pageSize,
  onPageChange,
  permissionsLoading,
  roleOptions,
  toolBarRender,
  refetchPermissions,
}: PermissionsTableProps) => {
  const isMobile = useIsMobile();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<any>(null);
  const getRoleMasterById = (id: string) => {
    return roleOptions?.find(
      (role: { value: string; label: string }) => role.value === id
    );
  };

  const handleEdit = (record: any) => {
    setEditingPermission(record?.props?.text ?? record);
    setEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingPermission(null);
  };

  const handleEditSuccess = () => {
    setEditModalOpen(false);
    setEditingPermission(null);
  };

  const handleDelete = async (record: any) => {
    const row = record?.props?.text ?? record;
    const response = await TenantPermissionService.deletePermission(row.id);
    if (response) {
      refetchPermissions();
    }
  };

  const permissionColumns = useMemo(
    () => [
      {
        title: "Schema",
        dataIndex: "schema_name",
        width: isMobile ? 120 : undefined,
        fixed: isMobile ? ("left" as const) : undefined,
        ellipsis: isMobile,
        className: isMobile ? "permission-cell-schema" : undefined,
        sorter: (a: any, b: any) => a.schema_name!.localeCompare(b.schema_name!),
      },
      {
        title: "Role",
        width: isMobile ? 148 : undefined,
        render: (record: any) => {
          const row = record?.props?.text ?? record;
          return (
            <div className="permission-cell-role" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontWeight: 500 }}>{getRoleMasterById(row.role)?.label}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <Checkbox checked={row.only_if_created} disabled />
                <span>Only If Created</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                <span>Is Active:</span>
                <ActiveToggleButton
                  table={tableName.permission}
                  pk={row.id}
                  is_active={row.is_active}
                />
              </div>
            </div>
          );
        },
      },
      {
        title: "Level",
        dataIndex: "level",
        width: isMobile ? 56 : undefined,
        className: isMobile ? "permission-cell-level" : undefined,
      },
      {
        title: "Permissions",
        width: isMobile ? 196 : undefined,
        render: (record: any) => {
          const row = record?.props?.text ?? record;
          return (
            <div className="permission-cell-perms" style={{ display: "flex", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: "#666" }}>Permissions</div>
                <PermCheck label="Read"   checked={row.can_read}   />
                <PermCheck label="Write"  checked={row.can_write}  />
                <PermCheck label="Create" checked={row.can_create} />
                <PermCheck label="Delete" checked={row.can_delete} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: "#666" }}>Additional</div>
                <PermCheck label="Select" checked={row.can_select} />
                <PermCheck label="Print"  checked={row.can_print}  />
                <PermCheck label="Export" checked={row.can_export} />
                <PermCheck label="Import" checked={row.can_import} />
                <PermCheck label="Email"  checked={row.can_email}  />
                <PermCheck label="Report" checked={row.can_report} />
                <PermCheck label="Share"  checked={row.can_share}  />
              </div>
            </div>
          );
        },
      },
      {
        title: "Actions",
        width: isMobile ? 72 : 120,
        fixed: isMobile ? ("right" as const) : undefined,
        valueType: "option",
        render: (record: any) => [
          <CustomButton
            key="edit"
            icon={<Edit2 size={14} />}
            type="text"
            size="small"
            onClick={() => handleEdit(record)}
            title="Edit Permission"
          />,
          <Popconfirm
            key="delete"
            title="Are you sure you want to delete this permission?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <CustomButton
              icon={<Trash size={14} />}
              type="text"
              size="small"
              danger
              title="Delete Permission"
            />
          </Popconfirm>,
        ],
      },
    ],
    [isMobile, roleOptions]
  );

  return (
    <>
      {isMobile && (
        <p className="permission-mobile-scroll-hint text-muted-foreground mb-2 text-xs">
          Swipe horizontally to see role, level and permissions.
        </p>
      )}
      <BaseTable
        loading={permissionsLoading}
        columns={permissionColumns as any}
        dataSource={permissions?.results ?? []}
        scroll={isMobile ? { x: 640 } : {}}
        options={isMobile ? false : true}
        headerTitle="Permissions Table"
        page={page}
        pageSize={pageSize}
        total={permissions?.count ?? 0}
        onPageChange={(page, size) => {
          onPageChange(page, size ?? 10);
        }}
        rowKey="id"
        tableName={gridName.permissionTable}
        request={refetchPermissions as any}
        toolBarRender={toolBarRender}
      />

      <PermissionModal
        open={editModalOpen}
        onCancel={handleEditCancel}
        onSuccess={handleEditSuccess}
        mode="edit"
        permissionId={editingPermission?.id ?? ""}
        initialData={editingPermission}
        refetchPermissions={refetchPermissions}
        selectedSchema={editingPermission?.schema_name ?? ""}
        selectedRole={editingPermission?.role ?? ""}
      />
    </>
  );
};

export default PermissionsTable;
